import {
  IControllerErrorReporter,
  IControllerFactory,
  EcomPlatformViewerScriptContext,
} from '../types';
import {
  IWidgetControllerConfig,
  IWidgetController,
} from '@wix/native-components-infra/dist/src/types/types';
import {
  getReleaseFromBaseUrl,
  ISentryErrorBoundaryPropsInjectedByViewerScript,
} from '@wix/native-components-infra';
import {
  createFakeRaven,
  withErrorReporting,
} from '../../error-reporter/errorReporter';

function emptyCtrl() {
  return {
    pageReady: /* istanbul ignore next */ (): any => {},

    exports: /* istanbul ignore next */ () => ({}),
  };
}

export async function initController(
  ctrlFactory: IControllerFactory | undefined,
  context: EcomPlatformViewerScriptContext,
  props: IWidgetControllerConfig,
) {
  if (!ctrlFactory) {
    return emptyCtrl();
  }

  const baseUrl =
    ctrlFactory.baseUrlKey &&
    context.siteStore.baseUrls[ctrlFactory.baseUrlKey];

  const release =
    baseUrl &&
    /* istanbul ignore next */ getReleaseFromBaseUrl(baseUrl, {
      artifactName: true,
    });

  const monitoring = ctrlFactory.sentryDSN
    ? context.createMonitoring!(ctrlFactory.sentryDSN, {
        release,
      })
    : createFakeRaven();

  const reportError: IControllerErrorReporter = (exception, options) => {
    /* istanbul ignore else */
    if (process.env.NODE_ENV === 'test') {
      console.error('Error in viewer script:\n', exception);
    }

    return monitoring(exception, options);
  };

  const setPropsWithErrorsReporting = (p: Record<string, unknown>) => {
    const errorBoundaryProps: ISentryErrorBoundaryPropsInjectedByViewerScript =
      {
        sentryRelease: release,
        ravenUserContextOverrides: {
          id: context.siteStore.storeId,
          uuid: context.siteStore.uuid,
        },
      };

    props.setProps({
      ...errorBoundaryProps,
      ...withErrorReporting(reportError)(p),
    });
  };

  const ctrl: IWidgetController = ctrlFactory.factory({
    config: props.config,
    compId: props.compId,
    context,
    setProps: setPropsWithErrorsReporting,
    platformAPIs: props.platformAPIs,
    reportError,
    type: props.type,
    warmupData: props.warmupData,
    wixCodeApi: props.wixCodeApi,
    controllerConfig: props,
    appData: {
      context,
      __prepopulated: {
        experiments: context.siteStore.experiments.all(),
        biLogger: context.siteStore.biLogger as any,
      },
      reportError,
    },
  });

  return withErrorReporting(reportError)(await ctrl);
}
